import React, { useState } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { ChatCompletionRequest } from "../../model_gen/chat";
import { RequestViewButton } from "../shared/button/Buttons";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Checkbox } from "../ui/checkbox";
import { message } from "antd";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";
import { 
  CopyIcon, 
  TagIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from "lucide-react";
import { cn } from "../../lib/utils";
import moment from "moment";

interface TestSetEligibleRequestsTableProps {
  requests: ChatCompletionRequest[];
  loading?: boolean;
  apiKey: ApiKeyOrGetter;
  selectedRequests: ChatCompletionRequest[];
  onSelectionChange: (requests: ChatCompletionRequest[]) => void;
  onRequestUpdate?: (request: ChatCompletionRequest) => void;
  className?: string;
  selectable?: boolean; // New prop to enable/disable selection
  showOnlySelected?: boolean; // New prop to show only selected requests
  // Pagination props
  currentPage?: number;
  totalPages?: number;
  totalItems?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
}

// Extended interface to include selection state for PaginatedTable
interface RequestWithSelection extends ChatCompletionRequest {
  _isSelected?: boolean;
}

const TestSetEligibleRequestsTable: React.FC<TestSetEligibleRequestsTableProps> = ({
  requests,
  loading = false,
  apiKey,
  selectedRequests,
  onSelectionChange,
  onRequestUpdate,
  className,
  selectable = false, // Default to false for backward compatibility
  showOnlySelected = false, // Default to false
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange
}) => {
  const [expandedTagRows, setExpandedTagRows] = useState<Set<string>>(new Set());

  // Get selected row keys for table selection
  const selectedRowKeys = selectedRequests.map(r => r.request_id);

  // Filter requests based on showOnlySelected and add selection state
  const displayedRequests: RequestWithSelection[] = (showOnlySelected ? selectedRequests : requests).map(request => ({
    ...request,
    _isSelected: selectedRowKeys.includes(request.request_id)
  }));

  const handleRowSelection = (requestId: string, isSelected: boolean) => {
    const request = requests.find(r => r.request_id === requestId);
    if (!request) return;

    if (isSelected) {
      const newRequest = {
        ...request,
        metadata: {
          ...request.metadata,
          _maitai_test_request_meta: JSON.stringify({ tags: [] }),
        },
      };
      onSelectionChange([...selectedRequests, newRequest]);
    } else {
      const newSelectedRequests = selectedRequests.filter(row => row.request_id !== requestId);
      onSelectionChange(newSelectedRequests);
    }
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      // Add all current page requests to selection
      const newRequests = displayedRequests
        .filter(request => !selectedRowKeys.includes(request.request_id))
        .map(request => ({
          ...request,
          metadata: {
            ...request.metadata,
            _maitai_test_request_meta: JSON.stringify({ tags: [] }),
          },
        }));
      onSelectionChange([...selectedRequests, ...newRequests]);
    } else {
      // Remove all current page requests from selection
      const currentPageIds = displayedRequests.map(r => r.request_id);
      const newSelectedRequests = selectedRequests.filter(
        row => !currentPageIds.includes(row.request_id)
      );
      onSelectionChange(newSelectedRequests);
    }
  };

  const handleTagsChange = (requestId: string, newTags: string[]) => {
    const updatedRequests = selectedRequests.map(row => {
      if (row.request_id === requestId) {
        return {
          ...row,
          metadata: {
            ...row.metadata,
            _maitai_test_request_meta: JSON.stringify({ tags: newTags }),
          },
        };
      }
      return row;
    });
    onSelectionChange(updatedRequests);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success("Request ID copied to clipboard!");
    }).catch(() => {
      message.error("Failed to copy request ID.");
    });
  };

  const formatDate = (timestamp: number) => {
    return moment(timestamp).format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number) => {
    return moment(timestamp).format("h:mm A");
  };

  const renderRequestId = (requestId: string) => {
    // Show last 8 characters with ellipsis prefix for better readability
    const truncatedId = `...${requestId.slice(-8)}`;
    return (
      <div className="flex items-center gap-2">
        <code className="text-sm font-mono bg-muted px-2 py-1 rounded text-foreground">
          {truncatedId}
        </code>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(requestId);
          }}
        >
          <CopyIcon className="w-3 h-3" />
        </Button>
      </div>
    );
  };

  const toggleTagExpansion = (requestId: string) => {
    const newExpanded = new Set(expandedTagRows);
    if (newExpanded.has(requestId)) {
      newExpanded.delete(requestId);
    } else {
      newExpanded.add(requestId);
    }
    setExpandedTagRows(newExpanded);
  };

  const renderTagsCell = (record: RequestWithSelection) => {
    const isSelected = selectedRowKeys.includes(record.request_id);
    const selectedRow = selectedRequests.find(
      row => row.request_id === record.request_id
    );
    let tags: string[] = [];
    let metadataTags: string[] = [];

    // Get tags from selected row (user-added tags)
    if (selectedRow?.metadata?._maitai_test_request_meta) {
      try {
        const meta = JSON.parse(
          selectedRow.metadata._maitai_test_request_meta as string
        );
        tags = meta.tags || [];
      } catch (e) {
        console.error("Error parsing metadata:", e);
      }
    }

    // Get suggested tags from original metadata
    if (record.metadata?._maitai_request_meta) {
      try {
        const metadata = JSON.parse(
          record.metadata._maitai_request_meta as string
        );
        metadataTags = metadata.tags || [];
      } catch (e) {
        console.error("Error parsing metadata:", e);
      }
    }

    const isExpanded = expandedTagRows.has(record.request_id);
    const maxTagsToShow = 3;

    if (isSelected) {
      const visibleTags = isExpanded ? tags : tags.slice(0, maxTagsToShow);
      const hasMoreTags = tags.length > maxTagsToShow;

      return (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {visibleTags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {tag}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newTags = tags.filter((_, i) => i !== index);
                    handleTagsChange(record.request_id, newTags);
                  }}
                  className="ml-1 text-xs hover:text-red-500"
                >
                  ×
                </button>
              </Badge>
            ))}
            {hasMoreTags && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTagExpansion(record.request_id);
                }}
              >
                {isExpanded ? (
                  <>
                    <ChevronUpIcon className="w-3 h-3 mr-1" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="w-3 h-3 mr-1" />
                    +{tags.length - maxTagsToShow} more
                  </>
                )}
              </Button>
            )}
          </div>
          <Input
            placeholder="Add tag and press Enter"
            className="text-xs h-6"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                const value = e.currentTarget.value.trim();
                if (value && !tags.includes(value)) {
                  handleTagsChange(record.request_id, [...tags, value]);
                  e.currentTarget.value = "";
                }
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
          {/* Show suggested tags from metadata */}
          {metadataTags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {metadataTags.filter(tag => !tags.includes(tag)).map((tag, index) => (
                <Badge 
                  key={index} 
                  variant="outline" 
                  className="text-xs cursor-pointer hover:bg-maitai-green hover:text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTagsChange(record.request_id, [...tags, tag]);
                  }}
                >
                  + {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      );
    }

    // Show suggested tags when not selected
    if (metadataTags.length > 0) {
      const visibleMetadataTags = isExpanded ? metadataTags : metadataTags.slice(0, maxTagsToShow);
      const hasMoreMetadataTags = metadataTags.length > maxTagsToShow;

      return (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1">
            {visibleMetadataTags.map((tag, index) => (
              <Badge key={index} variant="secondary" className="text-xs">
                {tag}
              </Badge>
            ))}
            {hasMoreMetadataTags && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleTagExpansion(record.request_id);
                }}
              >
                {isExpanded ? (
                  <>
                    <ChevronUpIcon className="w-3 h-3 mr-1" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDownIcon className="w-3 h-3 mr-1" />
                    +{metadataTags.length - maxTagsToShow} more
                  </>
                )}
              </Button>
            )}
          </div>
          {selectable && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRowSelection(record.request_id, true);
              }}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <TagIcon className="w-3 h-3" />
              Select to edit tags
            </button>
          )}
        </div>
      );
    }

    return selectable ? (
      <button
        onClick={(e) => {
          e.stopPropagation();
          handleRowSelection(record.request_id, true);
        }}
        className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
      >
        <TagIcon className="w-3 h-3" />
        Select to add tags
      </button>
    ) : (
      <span className="text-sm text-muted-foreground">No tags</span>
    );
  };

  // Calculate if all current page items are selected
  const currentPageIds = displayedRequests.map(r => r.request_id);
  const allCurrentPageSelected = currentPageIds.length > 0 && currentPageIds.every(id => selectedRowKeys.includes(id));

  // Define columns for PaginatedTable
  const columns: PaginatedTableColumn<RequestWithSelection>[] = [
    ...(selectable ? [{
      key: "selection" as keyof RequestWithSelection,
      header: (
        <Checkbox
          checked={allCurrentPageSelected}
          onCheckedChange={handleSelectAll}
        />
      ),
      width: "w-[50px]",
      render: (record: RequestWithSelection) => (
        <Checkbox
          checked={record._isSelected}
          onCheckedChange={(checked) => {
            handleRowSelection(record.request_id, !!checked);
          }}
        />
      )
    }] : []),
    {
      key: "date_created",
      header: "Date",
      width: selectable ? "w-[18%]" : "w-[20%]",
      render: (record: RequestWithSelection) => (
        <div className="space-y-1">
          <div className="text-sm font-gc-reg text-foreground">
            {formatDate(record.date_created)}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatTime(record.date_created)}
          </div>
        </div>
      )
    },
    {
      key: "request_id",
      header: "Request ID",
      width: selectable ? "w-[18%]" : "w-[20%]",
      render: (record: RequestWithSelection) => renderRequestId(record.request_id)
    },
    {
      key: "metadata" as keyof RequestWithSelection,
      header: "Tags",
      width: selectable ? "w-[32%]" : "w-[35%]",
      render: (record: RequestWithSelection) => (
        <div onClick={(e) => e.stopPropagation()}>
          {renderTagsCell(record)}
        </div>
      )
    },
    {
      key: "actions" as keyof RequestWithSelection,
      header: "Actions",
      width: "w-[15%]",
      render: (record: RequestWithSelection) => (
        <RequestViewButton
          buttonStyle={{ color: "#21b892" }}
          apiKey={apiKey}
          request={record}
          to={`/application/${
            record.application_id
          }/session/${encodeURIComponent(record.session_id)}/request/${
            record.request_id
          }`}
          isEditable={true}
          onUpdated={(req: ChatCompletionRequest) => {
            onRequestUpdate?.(req);
          }}
        >
        </RequestViewButton>
      )
    }
  ];

  const getRowClassName = (record: RequestWithSelection) => {
    return cn(
      record._isSelected && "bg-maitai-green/5"
    );
  };

  // Show custom pagination for showOnlySelected or when server-side pagination is not used
  const showCustomPagination = !showOnlySelected && totalPages > 1 && !!onPageChange;

  return (
    <div className={cn("space-y-4", className)}>
      <PaginatedTable
        data={displayedRequests}
        columns={columns}
        loading={loading}
        currentPage={currentPage}
        onPageChange={onPageChange || (() => {})}
        pageSize={itemsPerPage}
        totalItems={showOnlySelected ? selectedRequests.length : totalItems}
        emptyTitle="No eligible requests found"
        emptyDescription="There are no requests available for this intent group that can be added to a test set."
        rowClassName={getRowClassName}
        showPagination={showCustomPagination}
        loadingRows={5}
        serverSidePagination={!showOnlySelected}
      />
    </div>
  );
};

export default TestSetEligibleRequestsTable; 